/**
 * Varsom avalanche forecast (NVE), by coordinates and date — called directly from the browser
 * (the API sends `Access-Control-Allow-Origin: *`). Dates are Norwegian calendar days.
 */

const VARSOM_API = 'https://api01.nve.no/hydrology/forecast/avalanche/v6.3.2/api';

export interface VarsomProblem {
  typeId: number;
  typeName: string;
  causeName: string;
  sensitivity: string;
  probability: string;
  size: string;
  propagation: string;
  /** One flag per sector N, NE, E, SE, S, SW, W, NW. */
  aspects: boolean[];
  /** Elevation bands (m) the problem applies to. */
  bands: { min: number; max: number }[];
}

export interface VarsomForecast {
  regionName: string;
  date: string;
  /** 0 = no rating (e.g. off-season), 1–5 danger levels. */
  dangerLevel: number;
  dangerLevelName: string;
  mainText: string;
  problems: VarsomProblem[];
  url: string;
}

interface VarsomRaw {
  RegionName: string;
  RegionId: number;
  DangerLevel: string;
  DangerLevelName: string;
  MainText: string | null;
  ValidFrom: string;
  AvalancheProblems:
    | {
        AvalancheProblemTypeId: number;
        AvalancheProblemTypeName: string;
        AvalCauseName: string | null;
        AvalTriggerSensitivityName: string | null;
        AvalProbabilityName: string | null;
        DestructiveSizeExtName: string | null;
        AvalPropagationName: string | null;
        ValidExpositions: string | null;
        ExposedHeight1: number;
        ExposedHeight2: number;
        ExposedHeightFill: number;
      }[]
    | null;
}

const TOP = 10_000;

/**
 * Elevation bands from Varsom's exposed-height fields. Fill 1: above height 1; 2: below height 1;
 * 3: below height 1 and above height 2 (the middle is excluded); 4: between the two.
 */
export function exposedBands(h1: number, h2: number, fill: number): { min: number; max: number }[] {
  const lo = Math.min(h1, h2);
  const hi = Math.max(h1, h2);
  switch (fill) {
    case 1:
      return [{ min: h1, max: TOP }];
    case 2:
      return [{ min: 0, max: h1 }];
    case 3:
      return [
        { min: 0, max: lo },
        { min: hi, max: TOP },
      ];
    case 4:
      return [{ min: lo, max: hi }];
    default:
      return [{ min: 0, max: TOP }];
  }
}

export function parseVarsom(raw: VarsomRaw, date: string): VarsomForecast {
  return {
    regionName: raw.RegionName,
    date,
    dangerLevel: Number(raw.DangerLevel) || 0,
    dangerLevelName: raw.DangerLevelName,
    mainText: raw.MainText ?? '',
    problems: (raw.AvalancheProblems ?? []).map((p) => ({
      typeId: p.AvalancheProblemTypeId,
      typeName: p.AvalancheProblemTypeName,
      causeName: (p.AvalCauseName ?? '').trim(),
      sensitivity: (p.AvalTriggerSensitivityName ?? '').trim(),
      probability: (p.AvalProbabilityName ?? '').trim(),
      size: (p.DestructiveSizeExtName ?? '').trim(),
      propagation: (p.AvalPropagationName ?? '').trim(),
      aspects: Array.from({ length: 8 }, (_, k) => p.ValidExpositions?.[k] === '1'),
      bands: exposedBands(p.ExposedHeight1, p.ExposedHeight2, p.ExposedHeightFill),
    })),
    url: `https://www.varsom.no/en/snow/forecast/warning/${encodeURIComponent(raw.RegionName)}/${date}/`,
  };
}

export async function fetchVarsom(
  lat: number,
  lon: number,
  date: string,
  signal?: AbortSignal,
): Promise<VarsomForecast | null> {
  const url = `${VARSOM_API}/AvalancheWarningByCoordinates/Detail/${lat.toFixed(4)}/${lon.toFixed(4)}/2/${date}/${date}`;
  const res = await fetch(url, signal ? { signal } : {});
  if (!res.ok) throw new Error(`Varsom: HTTP ${res.status}`);
  const list = (await res.json()) as VarsomRaw[];
  const first = list[0];
  return first ? parseVarsom(first, date) : null;
}
